import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useAuth } from "./useAuth";
import { maintenanceRepository } from "@/repositories/maintenance";
import { usePermissions } from "@/hooks/usePermissions";
import {
  assertPhoneAvailableAcrossCompanies,
  assertNationalIdAvailableAcrossCompanies,
  assertEmailAvailableAcrossCompanies,
} from "@/lib/crossCompanyCheck";
import { useLanguage } from "@/contexts/LanguageContext";
import { buildPhoneCandidates } from "@/lib/phone";
import { contractsRepository } from "@/repositories/contracts";
import { invoicesRepository } from "@/repositories/invoices";
import { nafathVerificationsRepository } from "@/repositories/nafathVerifications";
import { tenantsRepository } from "@/repositories/tenants";
import { identityReserveUser } from "@/services/api";
import { API_URL } from "@/services/api";

export interface Tenant {
  id: string;
  company_id: string;
  tenant_type: "individual" | "commercial";
  first_name: string;
  last_name: string;
  first_name_en?: string;
  last_name_en?: string;
  commercial_name?: string;
  commercial_name_en?: string;
  unified_number?: string;
  cr_number?: string;
  /** VAT registration number (buyer-side; commercial tenants only). */
  vat_number?: string;
  phone: string;
  alternative_phone?: string;
  email?: string;
  id_type: "national" | "resident" | "gcc" | "passport" | "diplomat" | "visitor" | "premium_resident" | "other";
  id_number: string;
  id_image_url?: string;
  date_of_birth?: string;
  nationality?: string;
  address?: string;
  short_address?: string;
  national_address?: string;
  country?: string;
  region?: string;
  city?: string;
  district?: string;
  street_name?: string;
  building_number?: string;
  additional_code?: string;
  postal_code?: string;
  notification_channel: "email" | "sms" | "both" | "none";
  user_id?: string;
  invitation_status?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** Nafath verification data, attached by the list query for verified tenants. */
  nafath?: Record<string, unknown> | null;
}

export type TenantInsert = Omit<Tenant, "id" | "created_at" | "updated_at">;

/**
 * Result of `useCreateTenant`. `_reused: true` means an existing same-company
 * tenant was detected and reused (or an invite-placeholder reactivated) instead
 * of inserting a new row — so the caller can message accordingly. Structural
 * (id + phone are what consumers read) to absorb the create/update/getById row
 * shapes uniformly.
 */
export type CreateTenantResult = {
  id: string;
  phone?: string | null;
  _reused: boolean;
} & Record<string, unknown>;

export function useTenants() {
  const { companyId, user } = useAuth();
  const { hasPermission } = usePermissions();

  // Permission gate only — tenants are NOT scoped by employee property
  // assignments. An employee with tenants.view sees every tenant in the
  // company. Property-assignment scoping still applies to property/unit/
  // contract-bound resources, but not to the people directories.
  const canViewTenants = hasPermission("tenants.view");

  return useQuery({
    queryKey: ["tenants", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      if (!canViewTenants) return [];

      // Read from the tenants_with_status view (via repo) so `invitation_status`
      // reflects the derived value (not_invited / not_verified / verified). The
      // view exposes every column of `tenants` plus a `status` field; we copy
      // that over the legacy `invitation_status` so consumers don't need to change.
      // Also batch-fetches nafath_verifications for verified tenants (via repo).
      const rows = await tenantsRepository.listWithNafath(companyId);
      return (rows as unknown as (Tenant & { status?: string })[]).map((t) => ({
        ...t,
        invitation_status: t.status || "not_invited",
      })) as Tenant[];
    },
    // Gate on an authenticated user, not just a cached companyId. The
    // `tenants_with_status` view grants SELECT to authenticated/service_role only
    // (tenant data isn't public), so firing this while the session is `anon`
    // (e.g. a token-expiry / re-auth window on /auth, where companyId is still
    // cached) throws a hard "permission denied for view tenants_with_status".
    // Also gate on canViewTenants permission — checking inside queryFn still allows
    // the query to execute (and hit RLS/view permission errors) before the early
    // return. Moving the check to enabled prevents the query from running at all.
    enabled: !!user?.id && !!companyId && canViewTenants,
  });
}

// Hook to fetch a single tenant's full record BY ID (tenants table row,
// select '*') — used by master-invoice billed-to resolution to read
// vat_number/cr_number for a commercial-tenant buyer party (issue #3096).
// Mirrors useOwnerFullRecord (useOwners.ts).
export function useTenantFullRecord(tenantId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenant-full-record", tenantId, companyId],
    queryFn: () =>
      tenantId && companyId ? tenantsRepository.getById(tenantId, companyId) : null,
    enabled: !!tenantId && !!companyId,
  });
}

export function useTenantsStats() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenants-stats", companyId],
    queryFn: async () => {
      if (!companyId) return { total: 0, active: 0, inactive: 0 };
      return tenantsRepository.stats(companyId);
    },
    enabled: !!companyId,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { language } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async (tenant: Omit<TenantInsert, "company_id">): Promise<CreateTenantResult> => {
      if (!companyId) {
        throw new Error("No company ID found");
      }

      await assertPhoneAvailableAcrossCompanies(tenant.phone, companyId, language as "ar" | "en");
      await assertNationalIdAvailableAcrossCompanies(tenant.id_number, companyId, language as "ar" | "en");
      await assertEmailAvailableAcrossCompanies(tenant.email, companyId, language as "ar" | "en");

      // Detect & reuse an existing same-company tenant before inserting, so a
      // re-add — or an orphan row left behind by a failed prior invite —
      // reuses the record instead of colliding on the global-unique index (the
      // "هذه البيانات مستخدمة بالفعل" / "data is used" trap). Cross-company
      // duplicates were already rejected by the asserts above, so any match
      // here is same-company and safe to reuse.
      const reuseCandidates = tenant.phone ? buildPhoneCandidates(tenant.phone) : [];
      const existing = reuseCandidates.length
        ? await tenantsRepository.findReusableByPhone(companyId, reuseCandidates)
        : null;
      if (existing) {
        // The ONLY reliable orphan signal is the `INVITED-<phone>` placeholder
        // id_number set exclusively by useUnifiedInvite's invite path. A real
        // tenant added via this dialog also starts as invitation_status="invited"
        // with user_id=null (until they accept), so keying off those would
        // silently OVERWRITE a real tenant's data on a re-add. Real tenants
        // always carry a genuine id_number.
        const isPlaceholder = (existing.id_number ?? "").startsWith("INVITED-");
        // Reactivate/upgrade an invite-placeholder with the real entered data;
        // for a fully-real existing tenant, reuse it as-is (never overwrite).
        const reused = isPlaceholder
          ? await tenantsRepository.update(existing.id, { ...tenant }, companyId)
          : await tenantsRepository.getById(existing.id, companyId);
        return { ...(reused ?? existing), _reused: true } as CreateTenantResult;
      }

      const result = await tenantsRepository.create({ ...tenant, company_id: companyId });

      // Phase 1: reserve a user identity (non-blocking)
      if (result?.id && result.phone && API_URL) {
        const fullName = "full_name" in tenant
          ? (tenant as Record<string, unknown>).full_name as string
          : `${tenant.first_name} ${tenant.last_name}`.trim();
        try {
          const { user_id } = await identityReserveUser({
            phone: `+${tenant.phone}`,
            national_id: ("id_number" in tenant ? tenant.id_number : undefined) as string | undefined,
            full_name: fullName,
            // Park-at-create: grant the tenant role + company up front so the
            // person can log in immediately and shows correctly in admin views.
            company_id: companyId,
            role: "tenant",
            custom_role: "tenant",
          });
          await tenantsRepository.update(result.id, { user_id }, companyId);
        } catch {
          // Non-blocking — the fuzzy fallback at invite-accept still covers this.
        }
      }

      return { ...result, _reused: false } as CreateTenantResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenants-stats"] });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();
  const { language } = useLanguage();

  return useDemoSafeMutation({
    mutationFn: async ({ id, ...updates }: Partial<Tenant> & { id: string }) => {
      if (!companyId) throw new Error("No company ID");

      if (updates.phone) {
        await assertPhoneAvailableAcrossCompanies(updates.phone, companyId, language as "ar" | "en");
      }
      if (updates.id_number) {
        await assertNationalIdAvailableAcrossCompanies(updates.id_number, companyId, language as "ar" | "en");
      }
      if (updates.email) {
        await assertEmailAvailableAcrossCompanies(updates.email, companyId, language as "ar" | "en");
      }

      const data = await tenantsRepository.update(id, updates, companyId);
      if (!data) throw new Error("Tenant not found or access denied");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenants-stats"] });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (id: string) => {
      if (!companyId) throw new Error("No company ID");
      await tenantsRepository.softDelete(id, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenants-stats"] });
    },
  });
}

// Hook to deactivate a tenant (soft-delete)
export function useDeactivateTenant() {
  const queryClient = useQueryClient();
  const { companyId } = useAuth();

  return useDemoSafeMutation({
    mutationFn: async (tenantId: string) => {
      if (!companyId) throw new Error("No company ID");
      await tenantsRepository.deactivate(tenantId, companyId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      queryClient.invalidateQueries({ queryKey: ["tenants-stats"] });
      queryClient.invalidateQueries({ queryKey: ["users-stats"] });
    },
  });
}

// Get tenant with related data for detail page
export function useTenantDetails(id: string | undefined) {
  const { companyId, user } = useAuth();

  return useQuery({
    queryKey: ["tenant-details", id, companyId],
    queryFn: async () => {
      if (!id || !companyId) return null;
      // Read from tenants_with_status view (via the repository) so we get the
      // derived `status` field (verified / not_verified / not_invited), then
      // fetch the matched nafath_verifications row for verified tenants.
      const data = await tenantsRepository.getWithStatusById(id, companyId);
      if (!data) return null;

      const tenant = data as unknown as Tenant & { status?: string };
      let nafathRow: Record<string, unknown> | null = null;
      if (tenant.status === "verified" && tenant.id_type === "national") {
        const nv = await nafathVerificationsRepository.getCompletedByNationalId(
          tenant.id_number,
          companyId,
        );
        nafathRow = (nv as unknown as Record<string, unknown> | null) ?? null;
      }

      return {
        ...tenant,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        invitation_status: tenant.status ?? (tenant as any).invitation_status ?? "not_invited",
        nafath: nafathRow,
      } as Tenant & { nafath?: Record<string, unknown> | null };
    },
    enabled: !!user?.id && !!id && !!companyId,
  });
}

// Get tenant financial summary
export interface TenantFinancialSummary {
  totalPaid: number;
  amountDue: number;
  overduePayments: number;
  invoicesCount: number;
}

export function useTenantFinancials(tenantId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenant-financials", tenantId, companyId],
    queryFn: async (): Promise<TenantFinancialSummary> => {
      if (!tenantId || !companyId) {
        return { totalPaid: 0, amountDue: 0, overduePayments: 0, invoicesCount: 0 };
      }

      // Get contracts for this tenant
      const contracts = await contractsRepository.listIdsByTenant(
        tenantId,
        companyId,
      );

      if (!contracts || contracts.length === 0) {
        return { totalPaid: 0, amountDue: 0, overduePayments: 0, invoicesCount: 0 };
      }

      const contractIds = contracts.map((c) => c.id);

      // Get invoices for these contracts
      const invoices = await invoicesRepository.listFinancialSummaryByContracts(
        companyId,
        contractIds,
      );

      const today = new Date().toISOString().split("T")[0];
      
      const totalPaid = invoices?.reduce((sum, i) => sum + (i.paid_amount || 0), 0) || 0;
      const amountDue = invoices?.reduce((sum, i) => sum + (i.balance || 0), 0) || 0;
      const overduePayments = invoices?.filter(
        (i) => i.status !== "paid" && i.due_date < today
      ).reduce((sum, i) => sum + (i.balance || 0), 0) || 0;

      return {
        totalPaid,
        amountDue,
        overduePayments,
        invoicesCount: invoices?.length || 0,
      };
    },
    enabled: !!tenantId && !!companyId,
  });
}

// Get tenant invoices for payment history
export function useTenantInvoices(tenantId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenant-invoices", tenantId, companyId],
    queryFn: async () => {
      if (!tenantId || !companyId) return [];

      // Get contracts for this tenant
      const contracts = await contractsRepository.listIdsByTenant(
        tenantId,
        companyId,
      );

      if (!contracts || contracts.length === 0) return [];

      const contractIds = contracts.map((c) => c.id);

      return invoicesRepository.listHistoryByContracts(companyId, contractIds);
    },
    enabled: !!tenantId && !!companyId,
  });
}

// Get tenant maintenance requests
export function useTenantMaintenanceRequests(tenantId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenant-maintenance", tenantId, companyId],
    queryFn: async () => {
      if (!tenantId || !companyId) return [];
      return maintenanceRepository.listByTenantIdForStaff(tenantId, companyId);
    },
    enabled: !!tenantId && !!companyId,
  });
}

// Enhanced stats with active contracts
export function useTenantStatsEnhanced() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["tenants-stats-enhanced", companyId],
    queryFn: async () => {
      if (!companyId) return { total: 0, withActiveContracts: 0, withoutContracts: 0, invited: 0 };

      // Read derived status from the view (via repo) so the "invited" count
      // actually means "invited but not yet verified", not the stale default.
      const tenants = await tenantsRepository.statsEnhanced(companyId);
      if (!tenants.length) return { total: 0, withActiveContracts: 0, withoutContracts: 0, invited: 0 };

      // Get active contracts
      const today = new Date().toISOString().split("T")[0];
      const activeContracts = await contractsRepository.listActiveTenantIds(
        companyId,
        today,
      );

      const tenantsWithActiveContracts = new Set(activeContracts?.map((c) => c.tenant_id) || []);

      const total = tenants.length;
      const withActiveContracts = tenants.filter((t) => tenantsWithActiveContracts.has(t.id)).length;
      const withoutContracts = total - withActiveContracts;
      const invited = tenants.filter((t: { status?: string }) => t.status === "not_verified").length;

      return { total, withActiveContracts, withoutContracts, invited };
    },
    enabled: !!companyId,
  });
}
